import React, { useRef, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Button,
    TextField,
    Typography,
    Slider,
    Grid,
    Paper,
    MenuItem,
    InputLabel,
    Select,
    FormControl,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    canvas: {
        border: "1px solid #ccc",
        cursor: "default",
        backgroundColor: "#fff",
    },
    controls: {
        padding: theme.spacing(2),
    },
}));

const fonts = ["Arial", "Verdana", "Georgia", "Courier", "Tahoma", "Impact"];

const CanvasEditor = ({
    imageUrl,
    frameUrl,
    initialName = "Nombre Apellido",
    initialNumber = "123",
    logoUrl = null,
}) => {
    const classes = useStyles();
    const canvasRef = useRef();
    const [scale, setScale] = useState(1);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = useState(null);
    const [dragType, setDragType] = useState(null);

    const [name, setName] = useState(initialName);
    const [number, setNumber] = useState(initialNumber);
    const [namePos, setNamePos] = useState({ x: 300, y: 300 });
    const [numberPos, setNumberPos] = useState({ x: 300, y: 350 });
    const [fontSizeNumber, setFontSizeNumber] = useState(28);
    const [fontSizeName, setFontSizeName] = useState(24);
    const [textColor, setTextColor] = useState("#ffffff");
    const [fontFamily, setFontFamily] = useState("Arial");

    const [logoImg, setLogoImg] = useState(null);
    const [logoPos, setLogoPos] = useState({ x: 400, y: 100 });
    const [logoScale, setLogoScale] = useState(0.5);

    const [baseImg, setBaseImg] = useState(null);
    const [frameImg, setFrameImg] = useState(null);

    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imageUrl;
        img.onload = () => setBaseImg(img);
    }, [imageUrl]);

    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = frameUrl;
        img.onload = () => setFrameImg(img);
    }, [frameUrl]);

    useEffect(() => {
        if (!logoUrl) return;
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = logoUrl;
        img.onload = () => setLogoImg(img);
    }, [logoUrl]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!baseImg || !frameImg) return;

        ctx.drawImage(
            baseImg,
            dragOffset.x,
            dragOffset.y,
            baseImg.width * scale,
            baseImg.height * scale
        );

        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);

        if (logoImg) {
            const logoW = logoImg.width * logoScale;
            const logoH = logoImg.height * logoScale;
            ctx.drawImage(logoImg, logoPos.x, logoPos.y, logoW, logoH);
        }

        ctx.fillStyle = textColor;
        ctx.textAlign = "center";

        ctx.font = `bold ${fontSizeNumber}px ${fontFamily}`;
        ctx.fillText(number, numberPos.x, numberPos.y);

        ctx.font = `bold ${fontSizeName}px ${fontFamily}`;
        ctx.fillText(name, namePos.x, namePos.y);
    }, [
        baseImg,
        frameImg,
        logoImg,
        scale,
        dragOffset,
        name,
        number,
        namePos,
        numberPos,
        fontSizeNumber,
        fontSizeName,
        textColor,
        fontFamily,
        logoPos,
        logoScale,
    ]);

    const detectDragTarget = (x, y) => {
        const logoW = logoImg ? logoImg.width * logoScale : 0;
        const logoH = logoImg ? logoImg.height * logoScale : 0;

        if (
            logoImg &&
            x >= logoPos.x &&
            x <= logoPos.x + logoW &&
            y >= logoPos.y &&
            y <= logoPos.y + logoH
        )
            return "logo";

        if (
            Math.abs(x - numberPos.x) < 50 &&
            Math.abs(y - numberPos.y) < 30
        )
            return "number";

        if (Math.abs(x - namePos.x) < 50 && Math.abs(y - namePos.y) < 30)
            return "name";

        return "image";
    };

    const handleMouseDown = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setDragType(detectDragTarget(x, y));
        setDragStart({ x, y });
    };

    const handleMouseMove = (e) => {
        if (!dragStart || !dragType) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const dx = x - dragStart.x;
        const dy = y - dragStart.y;
        setDragStart({ x, y });

        if (dragType === "image") setDragOffset((p) => ({ x: p.x + dx, y: p.y + dy }));
        else if (dragType === "logo") setLogoPos((p) => ({ x: p.x + dx, y: p.y + dy }));
        else if (dragType === "name") setNamePos((p) => ({ x: p.x + dx, y: p.y + dy }));
        else if (dragType === "number") setNumberPos((p) => ({ x: p.x + dx, y: p.y + dy }));
    };

    const handleMouseUp = () => {
        setDragStart(null);
        setDragType(null);
    };

    const downloadImage = () => {
        const link = document.createElement("a");
        link.download = "composicion.png";
        link.href = canvasRef.current.toDataURL();
        link.click();
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
                <canvas
                    ref={canvasRef}
                    width={600}
                    height={600}
                    className={classes.canvas}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <Paper className={classes.controls}>
                    <Typography variant="h6">Controles</Typography>

                    <TextField label="Nombre" fullWidth value={name} onChange={(e) => setName(e.target.value)} margin="dense" />
                    <TextField label="Número" fullWidth value={number} onChange={(e) => setNumber(e.target.value)} margin="dense" />

                    <FormControl fullWidth margin="dense">
                        <InputLabel>Fuente</InputLabel>
                        <Select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                            {fonts.map((font) => (
                                <MenuItem key={font} value={font}>{font}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField label="Color de texto" type="color" fullWidth value={textColor} onChange={(e) => setTextColor(e.target.value)} margin="dense" />

                    <Typography gutterBottom>Zoom Imagen</Typography>
                    <Slider min={0.2} max={3} step={0.1} value={scale} onChange={(e, val) => setScale(val)} />

                    <Typography gutterBottom>Tamaño fuente número</Typography>
                    <Slider min={10} max={200} step={1} value={fontSizeNumber} onChange={(e, val) => setFontSizeNumber(val)} />

                    <Typography gutterBottom>Tamaño fuente nombre</Typography>
                    <Slider min={10} max={200} step={1} value={fontSizeName} onChange={(e, val) => setFontSizeName(val)} />

                    {logoImg && (
                        <>
                            <Typography gutterBottom>Escala del logo</Typography>
                            <Slider min={0.1} max={2} step={0.05} value={logoScale} onChange={(e, val) => setLogoScale(val)} />
                        </>
                    )}

                    <Button variant="contained" color="primary" fullWidth onClick={downloadImage} style={{ marginTop: "1rem" }}>
                        Descargar Imagen
                    </Button>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CanvasEditor;
